﻿import {isNullOrEmpty, NamedValue, StringNamedValue, translation} from "@2d-front-monorepo/core";
import {
	initializeMvvmContext,
	MvvmDataProvider,
	MvvmDataProviderRef,
	MvvmSelect
} from "@2d-front-monorepo/design-library";
import {RequiredVehicleProperties, VehicleResult} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, RefObject, useMemo} from "react";
import {Attachments} from "../Attachments";
import AttachmentType from "../enums/AttachmentType";
import {VehicleId} from "../nominalTypes/VehicleId";
import {ComponentParts, ComponentWithChildrenParts} from "./ComponentParts";
import CreateAttachmentsComponent from "./CreateAttachmentsComponent";
import CreateVehicleComponent, {CreateVehicleModel, PlateValidationRequirements} from "./CreateVehicleComponent";
import PermitsMvvmErrorCollector from "./PermitsMvvmErrorCollector";
import VehicleComponent from "./VehicleComponent";

export interface CreateOrSelectVehicleComponentProps extends ComponentParts {
	colors: StringNamedValue[];
	contextRef?: RefObject<MvvmDataProviderRef<CreateOrSelectVehicleModel>>;
	createEnabled?: boolean;
	createParts?: ComponentWithChildrenParts;
	filesPrefix: string;
	initialState: CreateOrSelectVehicleModel;
	models: StringNamedValue[];
	onSubmit: (formData: CreateOrSelectVehicleModel) => Promise<void>;
	optionalAttachments?: AttachmentType[];
	plateValidation: PlateValidationRequirements;
	provinces: StringNamedValue[];
	readOnlyProperties?: RequiredVehicleProperties;
	requiredAttachments: AttachmentType[];
	requiredProperties: RequiredVehicleProperties;
	hiddenProperties?: RequiredVehicleProperties;
	selectParts?: ComponentWithChildrenParts;
	vehicles?: VehicleResult[];
}

export type CreateOrSelectVehicleModel = {
	vehicleId: VehicleId;
} & CreateVehicleModel;

function createSelectItems(vehicles: VehicleResult[] | undefined, createEnabled: boolean | undefined): NamedValue<VehicleId>[] {
	const result: NamedValue<VehicleId>[] = [];
	if (vehicles) {
		result.push(...vehicles.map(r => {
			return {value: r.id, label: r.plate}
		}));
	}

	if (createEnabled) {
		result.push({
			label: translation(PermitTranslations.add_new),
			value: "create"
		});
	}

	return result;
}

export default function CreateOrSelectVehicleComponent(props: CreateOrSelectVehicleComponentProps): JSX.Element {
	const {
		initialState,
		onSubmit,
		filesPrefix,
		vehicles,
		contextRef,
		createEnabled,
		colors,
		models,
		provinces,
		header,
		footer,
		plateValidation,
		requiredProperties,
		hiddenProperties,
		readOnlyProperties,
		requiredAttachments,
		optionalAttachments,
		createParts,
		selectParts
	} = props;

	const choose = useMemo(() => createSelectItems(vehicles, createEnabled), [vehicles, createEnabled]);
	const multiple = choose.length > 1;

	const context = initializeMvvmContext({
		initialState: () => {
			initialState.vehicleId = isNullOrEmpty(initialState.vehicleId) ? choose.length > 0 ? choose[0].value : "create" : initialState.vehicleId;
			return initialState;
		},
		contextRef: contextRef
	}, [initialState]);

	const state = context.state;
	const selectedVehicle = useMemo(() => vehicles?.find(r => r.id === state.vehicleId), [state.vehicleId]);
	const expiredAttachments = useMemo(() => Attachments.findExpiredTypes(selectedVehicle, requiredAttachments), [selectedVehicle, requiredAttachments]);
	const replaceableAttachments = useMemo(() => Attachments.findSubmitted(selectedVehicle), [selectedVehicle]);
	const createVisible = createEnabled && state.vehicleId === "create";

	return <MvvmDataProvider<CreateOrSelectVehicleModel> onSubmit={onSubmit} context={context}>
		<PermitsMvvmErrorCollector/>
		{header}
		{multiple && <MvvmSelect context={context}
                                 label={translation(PermitTranslations.new_vehicle__choose)}
                                 property="vehicleId"
                                 items={choose}/>}

		{selectedVehicle && <div className="layout vertical gap">
			{selectParts?.header}
            <VehicleComponent vehicle={selectedVehicle} filesPrefix={filesPrefix}/>
			{selectParts?.children}
            <CreateAttachmentsComponent context={context}
                                        label={translation(PermitTranslations.documentation__required)}
                                        types={expiredAttachments}
                                        required={true}/>

            <CreateAttachmentsComponent context={context}
                                        label={translation(PermitTranslations.documentation__update)}
                                        types={replaceableAttachments}
                                        required={false}/>
			{selectParts?.footer}
        </div>}

		{createVisible &&
            <CreateVehicleComponent context={context}
                                    requiredAttachments={requiredAttachments}
                                    optionalAttachments={optionalAttachments}
                                    requiredProperties={requiredProperties}
                                    hiddenProperties={hiddenProperties}
                                    readOnlyProperties={readOnlyProperties}
                                    colors={colors}
                                    models={models}
                                    provinces={provinces}
                                    plateValidation={plateValidation}
                                    header={createParts?.header}
                                    children={createParts?.children}
                                    footer={createParts?.footer}/>
		}

		{footer}
	</MvvmDataProvider>;
}
